import { get } from 'lodash';
import * as R from 'remeda';
import {
  add,
  addMinutes,
  endOfMonth,
  endOfWeek,
  format,
  isEqual,
  isSameDay,
  isSameMonth,
  isSameYear,
  isValid,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { dateFnsLocales } from '../i18n/dateLocales';

export const FIRST_DAY_OF_WEEK = 1;
export const getToday = () => new Date(Date.now());

export const getThisMonthStart = () => startOfMonth(getToday());
export const getThisWeekStart = () =>
  startOfWeek(getToday(), { weekStartsOn: FIRST_DAY_OF_WEEK });
export const getYesterday = () => add(getToday(), { days: -1 });
export const getLastWeekStart = () => add(getThisWeekStart(), { days: -7 });
export const getLastWeekEnd = () =>
  add(endOfWeek(getToday(), { weekStartsOn: FIRST_DAY_OF_WEEK }), {
    days: -7,
  });
export const getLastMonthStart = () => add(getThisMonthStart(), { months: -1 });
export const getLastMonthEnd = () =>
  endOfMonth(add(getToday(), { months: -1 }));

export const QUERY_FORMAT_WITH_SECONDS = 'yyyy-MM-dd HH:mm:ss';

export const formatDateHoursSecondsToQuery = (date: Date) =>
  format(date, QUERY_FORMAT_WITH_SECONDS);

type RangeType = {
  from: string;
  to: string;
};

type FormatingOptionsKey = 'sameDay' | 'sameMonth' | 'sameYear' | 'default';
type FormatingTimeOptionsKey = 'isEqual' | 'default';

type DatepickerFormats = Record<FormatingOptionsKey, RangeType>;

type DateTimeSecondsPickerFormats = Record<FormatingTimeOptionsKey, RangeType>;

const datepickerFormats: Record<string, DatepickerFormats> = {
  ja: {
    sameDay: { from: 'yo MMM do', to: 'yo MMM do' }, // 2000y 7 1d
    sameMonth: {
      // 2000y 6m 10d-15d
      from: 'yo MMM do',
      to: 'do',
    },
    sameYear: {
      // 2000y 6m 10d-7m 5d
      from: 'yo MMM do',
      to: 'MMM do',
    },
    default: { from: 'yo MMM do', to: 'yo MMM do' }, // 2000y 7 1d
  },
};

const defaultDatepickerFormats: DatepickerFormats = {
  sameDay: { from: 'd MMM, yyyy', to: 'd MMM, yyyy' }, // 1 Jan, 2020
  sameMonth: { from: 'd', to: 'd MMM, yyyy' }, // 1–31 Jan, 2020
  sameYear: { from: 'd MMM', to: 'd MMM, yyyy' }, // 1 Jan–29 Feb, 2020
  default: { from: 'd MMM, yyyy', to: 'd MMM, yyyy' }, // 24 Dec, 2019–15 Jan, 2020
};

const daterangeOptions: Array<{
  key: FormatingOptionsKey;
  condition: (from: Date, to: Date) => boolean;
}> = [
  { key: 'sameDay', condition: isSameDay },
  { key: 'sameMonth', condition: isSameMonth },
  { key: 'sameYear', condition: isSameYear },
];

const dateTimeRangeOptions: Array<{
  key: FormatingTimeOptionsKey;
  condition: (from: Date, to: Date) => boolean;
}> = [{ key: 'isEqual', condition: isEqual }];

export const baseFormat = (
  date: Date,
  options?: Parameters<typeof format>[2],
  formatString = 'd MMM, yyyy'
) =>
  format(date, formatString, options)
    .split(' ')
    .map(x => R.capitalize(x))
    .join(' '); // 1 Jan, 2020

export const formatDatepickerValue = (from: Date, to: Date, lang: string) => {
  const locale = get(dateFnsLocales, lang);
  if (!isValid(from) || !isValid(to)) {
    return '';
  }

  const formats = datepickerFormats[lang] || defaultDatepickerFormats;
  const key =
    daterangeOptions.find(daterangeOption =>
      daterangeOption.condition(from, to)
    )?.key ?? 'default';

  const fromFormatted = baseFormat(from, { locale }, formats[key].from);
  const toFormatted = baseFormat(to, { locale }, formats[key].to);

  return fromFormatted === toFormatted
    ? fromFormatted
    : `${fromFormatted}-${toFormatted}`;
};

const defaultDateTimeSecondsPickerFormats: DateTimeSecondsPickerFormats = {
  isEqual: {
    from: 'd MMM, yyyy HH:mm:ss',
    to: 'd MMM, yyyy HH:mm:ss',
  },
  default: {
    from: 'd MMM, yyyy HH:mm:ss',
    to: 'd MMM, yyyy HH:mm:ss',
  },
};

export const formatDateTimeWithSecondsPickerValue = (
  from: Date,
  to: Date,
  lang: string
) => {
  const locale = get(dateFnsLocales, lang);
  if (!isValid(from) || !isValid(to)) {
    return '';
  }

  const formats = defaultDateTimeSecondsPickerFormats;
  const key =
    dateTimeRangeOptions.find(dateTimeRangeOption =>
      dateTimeRangeOption.condition(from, to)
    )?.key ?? 'default';

  const fromFormatted = baseFormat(from, { locale }, formats[key].from);
  const toFormatted = baseFormat(to, { locale }, formats[key].to);

  return fromFormatted === toFormatted
    ? fromFormatted
    : `${fromFormatted}-${toFormatted}`;
};

export const convertToUTC = (date: Date) =>
  addMinutes(date, date.getTimezoneOffset());

export const convertFromUTC = (date: Date) =>
  addMinutes(date, -date.getTimezoneOffset());

export const addSecondsToDate = (date: Date, seconds: number) => {
  const newDate = new Date(date);
  newDate.setSeconds(date.getSeconds() + seconds);

  return newDate;
};
